import type { SkladLocation } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useRef, useState } from 'react';

type Prefill = {
  product_code: string;
  product_name: string;
  order_ref: string;
  stav: string;
  already: boolean;
  rows: { variant_name: string | null; qty: number }[];
  /** Co je na produkt ještě ve výrobě — per velikost a termín. */
  production: { variant_name: string | null; term_date: string; qty: number }[];
};

type DoneRow = {
  variant_name: string | null;
  qty: number;
  surplus: number;
  shoptet_ok: boolean;
};

type Pohyb = {
  key: string;
  created_at: number;
  product_code: string;
  variant_name: string | null;
  /** Skutečné variantní SKU („L1025/134"), ne kód + popisek velikosti. */
  sku: string;
  location: string;
  action: string;
  change: number;
  balance: string;
  created_by: string;
};

const INPUT =
  'rounded-[5px] border border-[#cbd5e1] bg-white px-3 py-2 text-sm font-semibold text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

// Množství se mění tlačítky −/+, nativní šipky v inputu jsou navíc.
const NO_SPIN =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

/** Klíč řádku — velikost, nebo prázdný řetězec u produktu bez variant. */
const rowKey = (variant: string | null) => variant ?? '';

/** 'YYYY-MM-DD' → '31.7.'. */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

/** Unix ms → '31.7. 14:05'. */
function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()}.${d.getMonth() + 1}. ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
}

/** Kde si pamatujeme volbu přepínače PAL (per prohlížeč / uživatel). */
const PAL_STORAGE_KEY = 'vyroba.naskladneni.pal';

/** Poslední volba přepínače; ve výchozím stavu zapnuto. */
function initialPal(): boolean {
  try {
    return localStorage.getItem(PAL_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

/**
 * Přepínač „Doplňovat PAL": kolegyně napíše „30" a myslí paletu „PAL30" —
 * prefix se doplní rovnou v políčku, ať je vidět, co půjde do ERP. Rozepsaný
 * prefix („PAL", „PAL ") ani ručně psaný název pozice se nepřepisuje, jinak by
 * si to s psaním a mazáním navzájem překáželo.
 */
function withPal(raw: string, pal: boolean): string {
  if (!pal) return raw;
  const v = raw.trimStart();
  if (v === '' || /^pal/i.test(v)) return v;
  return /^\d/.test(v) ? `PAL${v}` : v;
}

/** Opak `withPal` — po vypnutí přepínače se prefix zase sundá. */
function stripPal(raw: string): string {
  return raw.replace(/^pal\s*/i, '');
}

/**
 * Naskladnění celé zakázky — tlačítko „Naskladnit produkty" v detailu výrobní
 * zakázky. Kusy se předvyplní ze zakázky, u každé velikosti je vidět, co je na
 * ni ještě ve výrobě (a k jakému termínu). Backend zapíše do ERP, odečte kusy
 * z výroby a přebytek přičte do Shoptetu. Stav zakázky se NEmění — naskladnění
 * bývá i jen částečné, na „Hotovo" ji obsluha přepíná ručně, až je celá.
 */
export function NaskladneniZakazky({
  recordId,
  onClose,
  onDone,
}: {
  recordId: string;
  onClose: () => void;
  /** Naskladněno — nadřazený detail se má zavřít a tabulka přenačíst. */
  onDone: () => void;
}) {
  const [data, setData] = useState<Prefill | null>(null);
  const [locations, setLocations] = useState<SkladLocation[]>([]);
  const [movements, setMovements] = useState<Pohyb[]>([]);
  const [qtyByKey, setQtyByKey] = useState<Record<string, string>>({});
  const [posByKey, setPosByKey] = useState<Record<string, string>>({});
  const [bulkPos, setBulkPos] = useState('');
  const [pal, setPal] = useState(initialPal);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Naskladnění samo prošlo, jen se nepodařilo zapsat kartu do Trella —
  // ať to kolegyně vidí a založí ji ručně, ne že se to jen tiše ztratí.
  const [trelloWarn, setTrelloWarn] = useState<string | null>(null);
  const [result, setResult] = useState<DoneRow[] | null>(null);
  // Velikosti, u kterých se nepovedlo přičíst přebytek do e-shopu → oranžové okno.
  const shoptetChyby = (result ?? []).filter((r) => r.surplus > 0 && !r.shoptet_ok);
  // Řádky, které se právě naskladnily — chvíli svítí zeleně, pak zhasnou.
  const [doneKeys, setDoneKeys] = useState<Record<string, boolean>>({});
  useEscapeKey(onClose);
  // Políčka pozic — Tab mezi nimi skáče přímo, s tlačítky −/+ po cestě by to
  // bylo na tři stisky. Klíč je index řádku.
  const posRefs = useRef<(HTMLInputElement | null)[]>([]);

  const refreshMovements = useCallback(async (code: string) => {
    if (!code) return;
    try {
      const d = await apiFetch<{ movements: Pohyb[] }>(
        `/api/vyroba/pohyby?product_code=${encodeURIComponent(code)}&limit=60`,
      );
      setMovements(d.movements);
    } catch {
      /* log není kritický */
    }
  }, []);

  /** Po naskladnění se zůstatky ve výrobě mění — načti je znovu. */
  const refreshProduction = useCallback(async () => {
    try {
      const d = await apiFetch<Prefill>(`/api/vyroba/zakazka/${recordId}/naskladneni`);
      setData((prev) => (prev ? { ...prev, production: d.production } : prev));
    } catch {
      /* zůstatky jsou doplňková informace */
    }
  }, [recordId]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Prefill>(`/api/vyroba/zakazka/${recordId}/naskladneni`)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Velikost, která na zakázce není, zůstane prázdná (ne „0").
        const q: Record<string, string> = {};
        for (const r of d.rows) q[rowKey(r.variant_name)] = r.qty > 0 ? String(r.qty) : '';
        setQtyByKey(q);
        refreshMovements(d.product_code);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Chyba načítání zakázky'),
      );
    apiFetch<{ locations: SkladLocation[] }>('/api/vyroba/sklad/locations')
      .then((d) => {
        if (!cancelled) setLocations(d.locations);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Nepodařilo se načíst skladové pozice'),
      );
    return () => {
      cancelled = true;
    };
  }, [recordId, refreshMovements]);

  // Zelené řádky zhasnou najednou po 1,5 s (potvrzení, že zápis prošel).
  useEffect(() => {
    if (Object.keys(doneKeys).length === 0) return;
    const t = setTimeout(() => setDoneKeys({}), 1500);
    return () => clearTimeout(t);
  }, [doneKeys]);

  /** Pozice (název) → id z číselníku. Null, když název nesedí. */
  function locationIdByName(name: string): number | null {
    const n = name.trim();
    if (!n) return null;
    return locations.find((l) => l.name === n)?.id ?? null;
  }

  /** Pozice, kterou ERP nezná — políčko zčervená hned při psaní. */
  function unknownPos(raw: string): boolean {
    const v = raw.trim();
    return v !== '' && locations.length > 0 && locationIdByName(v) === null;
  }

  /** Co je na velikost ještě ve výrobě — „31.7. — 2 ks" za řádkem. */
  function productionFor(variant: string | null): { term_date: string; qty: number }[] {
    return (data?.production ?? []).filter((p) => (p.variant_name ?? '') === (variant ?? ''));
  }

  /** Přepnutí PAL přepíše i to, co je v polích už napsané (oběma směry). */
  function togglePal() {
    setPal((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PAL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* bez localStorage se volba prostě nezapamatuje */
      }
      const fix = (v: string) => (next ? withPal(v, true) : stripPal(v));
      setBulkPos(fix);
      setPosByKey((p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, fix(v)])));
      return next;
    });
  }

  /** Tab / Shift+Tab v poli pozice přeskočí rovnou na pozici dalšího řádku. */
  function posTab(e: React.KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key !== 'Tab') return;
    const next = posRefs.current[index + (e.shiftKey ? -1 : 1)];
    if (!next) return; // první / poslední řádek → normální chování
    e.preventDefault();
    next.focus();
    next.select();
  }

  function bump(key: string, delta: number) {
    setQtyByKey((p) => {
      const cur = Number.parseInt(p[key] ?? '', 10);
      const next = Math.max(0, (Number.isFinite(cur) ? cur : 0) + delta);
      return { ...p, [key]: String(next) };
    });
  }

  type BuiltRow = { variant_name: string | null; qty: number; stock_location_id: number };
  type BuildResult =
    | { ok: true; row: BuiltRow }
    | { ok: false; skip: true }
    | { ok: false; skip: false; error: string };

  /** Ověří jeden řádek (množství + pozice) a sestaví ho pro odeslání. */
  function buildRow(r: Prefill['rows'][number]): BuildResult {
    const key = rowKey(r.variant_name);
    const qty = Number.parseInt(qtyByKey[key] ?? '', 10);
    if (!Number.isInteger(qty) || qty <= 0) return { ok: false, skip: true };
    const pos = (posByKey[key] || bulkPos).trim();
    const locId = locationIdByName(pos);
    if (!locId) {
      return {
        ok: false,
        skip: false,
        error: `Neznámá skladová pozice „${pos || '(prázdné)'}" u velikosti ${r.variant_name ?? 'bez velikosti'}.`,
      };
    }
    return { ok: true, row: { variant_name: r.variant_name, qty, stock_location_id: locId } };
  }

  /** Odešle zadané řádky na server — sdílené pro „Naskladnit vše" i naskladnění jedné velikosti. */
  async function submitRows(rows: BuiltRow[]) {
    if (!data || rows.length === 0) return;
    setSubmitting(true);
    setErr(null);
    setTrelloWarn(null);
    try {
      const res = await apiFetch<{
        done: DoneRow[];
        trello?: { ok: boolean; cards: number; error?: string };
      }>(`/api/vyroba/zakazka/${recordId}/naskladnit`, {
        method: 'POST',
        body: { rows },
      });
      setResult(res.done);
      if (res.trello && !res.trello.ok) {
        setTrelloWarn(
          `Naskladněno, ale zápis do Trella selhal: ${res.trello.error ?? 'neznámá chyba'}`,
        );
      }
      // Naskladněné řádky probliknou zeleně a vyprázdní se (množství i pozice).
      const keys = rows.map((r) => rowKey(r.variant_name));
      setDoneKeys(Object.fromEntries(keys.map((k) => [k, true])));
      setQtyByKey((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [k, ''])) }));
      setPosByKey((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [k, ''])) }));
      await Promise.all([refreshMovements(data.product_code), refreshProduction()]);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Naskladnění selhalo');
      await Promise.all([refreshMovements(data.product_code), refreshProduction()]);
    } finally {
      setSubmitting(false);
    }
  }

  async function naskladnit() {
    if (!data) return;
    const rows: BuiltRow[] = [];
    for (const r of data.rows) {
      const built = buildRow(r);
      if (built.ok) rows.push(built.row);
      else if (!built.skip) {
        setErr(built.error);
        return;
      }
    }
    if (rows.length === 0) {
      setErr('Zadej množství aspoň u jedné velikosti.');
      return;
    }
    setErr(null);
    await submitRows(rows);
  }

  /** Naskladní jen jednu velikost — ať kolegyně nemusí kvůli tomu nulovat ostatní řádky. */
  async function naskladnitJednu(r: Prefill['rows'][number]) {
    const built = buildRow(r);
    if (!built.ok) {
      setErr(
        built.skip
          ? `Zadej množství u velikosti ${r.variant_name ?? 'bez velikosti'}.`
          : built.error,
      );
      return;
    }
    setErr(null);
    await submitRows([built.row]);
  }

  const celkem = data
    ? data.rows.reduce((s, r) => {
        const n = Number.parseInt(qtyByKey[rowKey(r.variant_name)] ?? '', 10);
        return s + (Number.isInteger(n) && n > 0 ? n : 0);
      }, 0)
    : 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <datalist id="zakazka-positions">
        {locations.map((l) => (
          <option key={l.id} value={l.name} />
        ))}
      </datalist>

      {result && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-8 text-center reveal">
            {shoptetChyby.length > 0 ? (
              <>
                {/* Kusy jsou v Brani i odečtené z výroby — chybí jen zápis do
                    e-shopu. Řeší se ručně, znovu naskladňovat se nesmí. */}
                <div className="mx-auto w-16 h-16 rounded-full bg-warning-light flex items-center justify-center text-4xl font-bold text-warning">
                  !
                </div>
                <h3 className="mt-4 text-xl font-bold tracking-wide text-warning">
                  NASKLADNĚNO, ALE…
                </h3>
                <div className="mt-3 rounded-lg border border-warning/40 bg-warning-light px-4 py-3 text-left text-sm text-text">
                  <p className="font-semibold text-warning">
                    Nepovedl se zápis do Shoptetu — doplň skladovost ručně:
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {shoptetChyby.map((r) => (
                      <li key={rowKey(r.variant_name)}>
                        <strong>
                          {data?.product_code}
                          {r.variant_name ? ` / ${r.variant_name}` : ''}
                        </strong>
                        : +{r.surplus} ks
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-text-secondary">
                    Na skladě (Brani) i ve výrobě je vše zapsané. Znovu nenaskladňuj — zapsalo by se
                    to podruhé.
                  </p>
                </div>
              </>
            ) : (
              <div className="mx-auto w-16 h-16 rounded-full bg-success-light flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-success"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
            )}
            {shoptetChyby.length === 0 && (
              <h3 className="mt-4 text-xl font-bold tracking-wide text-text">NASKLADNĚNO</h3>
            )}
            <ul className="mt-3 space-y-1 text-sm text-text text-left">
              {result.map((r) => (
                <li key={rowKey(r.variant_name)}>
                  <strong className="font-semibold">{r.variant_name ?? 'Bez velikosti'}</strong>:{' '}
                  {r.qty} ks na sklad
                  {r.surplus > 0
                    ? `, z toho ${r.surplus} ks nad rámec výroby ${
                        r.shoptet_ok ? 'přičteno do Shoptetu' : '— zápis do Shoptetu selhal'
                      }`
                    : ''}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-text-secondary">
              Kusy odečteny z výroby. Stav zakázky se automaticky nemění — pokud je celá vyřízená,
              přepni ji na „Hotovo" ručně.
            </p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="mt-6 w-full bg-action hover:bg-action-dark text-white text-base font-bold rounded-lg px-6 py-3 cursor-pointer transition-colors"
            >
              Pokračovat
            </button>
          </div>
        </div>
      )}

      <div className="relative w-full max-w-[77rem] my-6 bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-text">
            Naskladnění produktů k zakázce
            {data?.order_ref && <span className="text-text-muted">: {data.order_ref}</span>}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5">
          {err && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
              {err}
            </div>
          )}
          {trelloWarn && (
            <div className="text-sm text-warning bg-warning-light border border-warning/30 rounded-lg px-4 py-3 mb-4">
              {trelloWarn}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* ─── Vlevo: naskladnění ─────────────────────────────────────── */}
            <div>
              {data == null ? (
                <p className="text-sm text-text-muted">Načítám…</p>
              ) : (
                <>
                  <div className="text-sm text-text-secondary mb-4">
                    <span className="font-mono text-text-muted">{data.product_code}</span>{' '}
                    {data.product_name}
                  </div>

                  <label className="block">
                    <span className="block text-sm font-semibold text-text mb-1">
                      Cílová pozice hromadně
                    </span>
                    <input
                      list="zakazka-positions"
                      value={bulkPos}
                      onChange={(e) => setBulkPos(withPal(e.target.value, pal))}
                      placeholder={pal ? 'např. 30 → PAL30' : 'např. 01-01'}
                      className={`${INPUT} w-44 ${
                        unknownPos(bulkPos) ? 'border-danger text-danger' : ''
                      }`}
                    />
                    {unknownPos(bulkPos) && (
                      <span className="block text-xs text-danger mt-1">
                        Tuhle pozici sklad nezná — vyber ji z nabídky.
                      </span>
                    )}
                  </label>

                  {/* Přepínač „Doplňovat PAL" — „6" se hned přepíše na „PAL 6". */}
                  <button
                    type="button"
                    onClick={() => togglePal()}
                    className="mt-3 mb-4 flex items-center gap-2 cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-text">Doplňovat „PAL"</span>
                    <span
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                        pal ? 'bg-success' : 'bg-border'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                          pal ? 'left-[22px]' : 'left-0.5'
                        }`}
                      />
                    </span>
                  </button>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-text-secondary bg-bg">
                          <th className="px-2 py-1 rounded-l-md">Velikost</th>
                          <th className="px-2 py-1">Množství</th>
                          <th className="px-2 py-1">Skladová pozice</th>
                          <th className="px-2 py-1" />
                          <th className="px-2 py-1 rounded-r-md">Ve výrobě</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((r, rowIndex) => {
                          const key = rowKey(r.variant_name);
                          const inProduction = productionFor(r.variant_name);
                          const rowPos = posByKey[key] ?? '';
                          return (
                            <tr
                              key={key}
                              className={`border-b border-border-light transition-colors duration-700 ${
                                doneKeys[key] ? 'bg-success-light' : ''
                              }`}
                            >
                              <td className="px-2 py-0.5 font-bold text-text whitespace-nowrap">
                                {r.variant_name ?? 'Bez vel.'}
                              </td>
                              <td className="px-2 py-0.5">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => bump(key, -1)}
                                    aria-label="O kus míň"
                                    tabIndex={-1}
                                    className="h-6 w-6 shrink-0 rounded-[5px] border border-border bg-bg text-text-secondary font-bold leading-none hover:border-text-muted cursor-pointer"
                                  >
                                    −
                                  </button>
                                  <input
                                    type="number"
                                    value={qtyByKey[key] ?? ''}
                                    onChange={(e) =>
                                      setQtyByKey((p) => ({ ...p, [key]: e.target.value }))
                                    }
                                    className={`${INPUT} ${NO_SPIN} w-12 text-center px-1 py-0.5`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => bump(key, 1)}
                                    aria-label="O kus víc"
                                    tabIndex={-1}
                                    className="h-6 w-6 shrink-0 rounded-[5px] border border-border bg-bg text-text-secondary font-bold leading-none hover:border-text-muted cursor-pointer"
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td className="px-2 py-0.5">
                                <input
                                  list="zakazka-positions"
                                  value={posByKey[key] ?? ''}
                                  onChange={(e) =>
                                    setPosByKey((p) => ({
                                      ...p,
                                      [key]: withPal(e.target.value, pal),
                                    }))
                                  }
                                  ref={(el) => {
                                    posRefs.current[rowIndex] = el;
                                  }}
                                  onKeyDown={(e) => posTab(e, rowIndex)}
                                  placeholder={bulkPos.trim() || 'pozice'}
                                  title={
                                    unknownPos(rowPos) ? 'Tuhle pozici sklad nezná' : undefined
                                  }
                                  className={`${INPUT} w-24 font-normal py-0.5 ${
                                    unknownPos(rowPos) ? 'border-danger text-danger' : ''
                                  }`}
                                />
                              </td>
                              <td className="px-2 py-0.5">
                                <button
                                  type="button"
                                  onClick={() => naskladnitJednu(r)}
                                  disabled={
                                    submitting || !(Number.parseInt(qtyByKey[key] ?? '', 10) > 0)
                                  }
                                  title={`Naskladnit jen velikost ${r.variant_name ?? 'bez velikosti'}`}
                                  className="whitespace-nowrap rounded-[5px] bg-action hover:bg-action-dark text-white disabled:opacity-40 disabled:cursor-not-allowed font-bold px-3 py-2 text-xs cursor-pointer transition-colors"
                                >
                                  Naskladnit
                                </button>
                              </td>
                              <td className="px-2 py-0.5 text-xs text-text-secondary whitespace-nowrap">
                                {inProduction.map((p) => (
                                  <div key={p.term_date}>
                                    {fmtShort(p.term_date)} — {p.qty} ks
                                  </div>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 text-sm text-text">
                    Celkem <strong className="font-semibold">{celkem} ks</strong>
                  </div>
                  {data.already && !result && (
                    <p className="mt-3 rounded-lg border border-warning/40 bg-warning-light px-3 py-2 text-sm text-text">
                      Zakázka už je ve stavu „Hotovo" — tohle bude{' '}
                      <strong>opakované naskladnění</strong>. Kusy se přičtou znovu, tak zkontroluj
                      množství.
                    </p>
                  )}
                  <p className="mt-3 text-xs text-text-muted">
                    Zapíše se na skladovou pozici v ERP, kusy se odečtou z výroby (od nejstaršího
                    termínu) a co je nad rámec výroby, se přičte do dostupnosti na e-shopu. Stav
                    zakázky se sám nemění — na „Hotovo" ji přepni ručně, až bude celá vyřízená.
                  </p>
                </>
              )}
            </div>

            {/* ─── Vpravo: pohyby ve výrobním systému ─────────────────────── */}
            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border bg-bg">
                <h3 className="text-sm font-bold text-text">Pohyby ve výrobním systému</h3>
              </div>
              <div className="max-h-[28rem] overflow-y-auto overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-bg">
                    <tr className="text-left font-semibold text-text-secondary">
                      <th className="px-3 py-2">Datum</th>
                      <th className="px-3 py-2">Produkt</th>
                      <th className="px-3 py-2">Lokace</th>
                      <th className="px-3 py-2">Akce</th>
                      <th className="px-3 py-2 text-right">Změna</th>
                      <th className="px-3 py-2">Uživatel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                          Zatím žádné pohyby.
                        </td>
                      </tr>
                    ) : (
                      movements.map((m) => (
                        <tr key={m.key} className="border-t border-border-light">
                          <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                            {fmtDateTime(m.created_at)}
                          </td>
                          <td className="px-3 py-2 font-mono text-text whitespace-nowrap">
                            {/* Skutečné SKU z katalogu — popisek velikosti („134/140")
                                se nedá lepit za kód, vzniklo by „L1080/134/140". */}
                            {m.sku || m.product_code}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                            {m.location === 'E-shop' ? 'E-shop' : fmtShort(m.location)}
                          </td>
                          <td className="px-3 py-2 text-text-secondary">{m.action}</td>
                          <td
                            className={`px-3 py-2 text-right font-semibold ${
                              m.change < 0 ? 'text-danger' : 'text-success'
                            }`}
                          >
                            {m.change > 0 ? `+${m.change}` : m.change}
                          </td>
                          <td className="px-3 py-2 text-text-secondary truncate max-w-[9rem]">
                            {m.created_by}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          {result ? (
            <button
              type="button"
              onClick={onDone}
              className="bg-action hover:bg-action-dark text-white text-base font-bold rounded-[5px] px-8 py-3 cursor-pointer transition-colors"
            >
              Hotovo
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
              >
                Zrušit
              </button>
              <button
                type="button"
                onClick={naskladnit}
                disabled={submitting || data == null}
                className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-base font-bold rounded-[5px] px-8 py-3 cursor-pointer transition-colors"
              >
                {submitting ? 'Naskladňuji…' : 'Naskladnit vše'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
